export type AudioMode = 'musical' | 'scientific';
export type VelocityTimbre = 'sine' | 'softPad' | 'brightPad' | 'deepSpace' | 'choir' | 'shimmer' | 'glass' | 'darkDrone';
export type DistanceTimbre = 'warm' | 'choir' | 'shimmer' | 'deep' | 'sine';
export type AudioPalette = 'orbital' | 'luminous' | 'tense' | 'deep';

interface Voice {
  primary: OscillatorNode;
  secondary: OscillatorNode;
  secondaryLevel: GainNode;
  gain: GainNode;
  panner: StereoPannerNode;
}

interface TransientVoice {
  oscillator: OscillatorNode;
  gain: GainNode;
}

interface TimbreProfile {
  primary: OscillatorType;
  secondary: OscillatorType;
  secondaryRatio: number;
  secondaryLevel: number;
  detune: number;
  cutoff: number;
  resonance: number;
  ambience: number;
}

const MUSICAL_FREQUENCIES = [110, 146.83, 164.81, 220, 293.66, 329.63, 440, 587.33];
const SCIENTIFIC_FREQUENCIES = [160, 240, 320, 400, 480, 640, 800, 960];
// Nearest particles sound highest and most tense; distant bands settle into
// the lower register. These notes complement rather than duplicate the speed chord.
const MUSICAL_DISTANCE_FREQUENCIES = [659.25, 523.25, 440, 392, 329.63, 261.63, 220, 174.61];
const SCIENTIFIC_DISTANCE_FREQUENCIES = [960, 800, 640, 480, 400, 320, 240, 160];

const PALETTES: Record<AudioPalette, { root: number; ratios: number[] }> = {
  orbital: { root: 110, ratios: [1, 4 / 3, 3 / 2, 2, 8 / 3, 3, 4, 16 / 3] },
  luminous: { root: 123.47, ratios: [1, 5 / 4, 3 / 2, 2, 5 / 2, 3, 4, 5] },
  tense: { root: 98, ratios: [1, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 2, 12 / 5, 3] },
  deep: { root: 82.41, ratios: [1, 6 / 5, 3 / 2, 9 / 5, 2, 12 / 5, 3, 18 / 5] }
};

export class VelocitySonifier {
  private context?: AudioContext;
  private master?: GainNode;
  private velocityBus?: GainNode;
  private distanceBus?: GainNode;
  private velocityFilter?: BiquadFilterNode;
  private distanceFilter?: BiquadFilterNode;
  private velocityAmbienceSend?: GainNode;
  private distanceAmbienceSend?: GainNode;
  private ambienceWet?: GainNode;
  private recordingDestination?: MediaStreamAudioDestinationNode;
  private voices: Voice[] = [];
  private distanceVoices: Voice[] = [];
  private transientVoices: TransientVoice[] = [];
  private enabled = false;
  private mode: AudioMode = 'musical';
  private velocityTimbre: VelocityTimbre = 'softPad';
  private distanceTimbre: DistanceTimbre = 'warm';
  private palette: AudioPalette = 'orbital';
  private musicalFrequencies = [...MUSICAL_FREQUENCIES];
  private musicalDistanceFrequencies = [...MUSICAL_DISTANCE_FREQUENCIES];
  private volume = 0.3;
  private response = 0.45;
  private velocityEnabled = true;
  private distanceEnabled = true;
  private distanceMix = 0.45;
  private musicalMovement = 0.55;
  private eventDensity = 0.65;
  private rareAccentsEnabled = true;
  private spatialSourcesEnabled = true;
  private readonly previousVelocity = new Float32Array(8);
  private readonly previousDistance = new Float32Array(8);
  private hasPreviousHistogram = false;
  private nextTransientTime = 0;
  private currentInversion = -1;
  private nextAccentTime = 0;
  private accentVoice?: TransientVoice;

  get isEnabled(): boolean { return this.enabled; }

  getRecordingAudioTrack(): MediaStreamTrack | undefined {
    if (!this.enabled) return undefined;
    return this.recordingDestination?.stream.getAudioTracks()[0];
  }

  async setEnabled(enabled: boolean): Promise<void> {
    this.enabled = enabled;
    if (!enabled) {
      this.setMasterLevel(0);
      return;
    }
    this.ensureGraph();
    await this.context?.resume();
    this.applyMode();
    this.setDistanceBusLevel();
    this.setMasterLevel(this.volume);
  }

  setMode(mode: AudioMode): void {
    this.mode = mode;
    this.applyMode();
  }

  setVelocityTimbre(timbre: VelocityTimbre): void {
    this.velocityTimbre = timbre;
    this.applyMode();
  }

  setDistanceTimbre(timbre: DistanceTimbre): void {
    this.distanceTimbre = timbre;
    this.applyMode();
  }

  setPalette(palette: AudioPalette): void {
    this.palette = palette;
    const definition = PALETTES[palette];
    this.musicalFrequencies = definition.ratios.map((ratio) => definition.root * ratio);
    const distanceOctave = definition.root < 90 ? 4 : 2;
    this.musicalDistanceFrequencies = definition.ratios
      .slice()
      .reverse()
      .map((ratio) => definition.root * ratio * distanceOctave);
    this.applyMode();
  }

  setVolume(value: number): void {
    this.volume = Math.min(Math.max(value, 0), 1);
    if (this.enabled) this.setMasterLevel(this.volume);
  }

  setResponse(seconds: number): void {
    this.response = Math.min(Math.max(seconds, 0.05), 2);
  }

  setVelocityEnabled(enabled: boolean): void {
    this.velocityEnabled = enabled;
    this.setVelocityBusLevel();
  }

  setDistanceEnabled(enabled: boolean): void {
    this.distanceEnabled = enabled;
    this.setDistanceBusLevel();
  }

  setDistanceMix(value: number): void {
    this.distanceMix = Math.min(Math.max(value, 0), 1);
    this.setDistanceBusLevel();
  }

  setMusicalMovement(value: number): void {
    this.musicalMovement = Math.min(Math.max(value, 0), 1);
  }

  setEventDensity(value: number): void {
    this.eventDensity = Math.min(Math.max(value, 0), 1);
  }

  setRareAccentsEnabled(enabled: boolean): void {
    this.rareAccentsEnabled = enabled;
  }

  setSpatialSourcesEnabled(enabled: boolean): void {
    this.spatialSourcesEnabled = enabled;
    if (!enabled) this.setGravitySourcePans([]);
  }

  setGravitySourcePans(pans: number[]): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    this.distanceVoices.forEach((voice, index) => {
      const target = this.spatialSourcesEnabled && pans.length > 0 ? pans[index % pans.length] : -0.48;
      voice.panner.pan.setTargetAtTime(Math.min(Math.max(target, -0.9), 0.9), now, 0.12);
    });
  }

  update(velocityHistogram: Float32Array, distanceHistogram: Float32Array): void {
    if (!this.enabled || !this.context || this.context.state !== 'running') return;
    const now = this.context.currentTime;
    for (let index = 0; index < this.voices.length; index++) {
      const population = Math.min(Math.max(velocityHistogram[index] ?? 0, 0), 1);
      // Musical mode lifts quieter bands so the complete distribution can be
      // heard. Scientific mode preserves a direct, linear population mapping.
      const target = this.mode === 'musical'
        ? Math.sqrt(population) * 0.16
        : population * 0.72;
      const gain = this.voices[index].gain.gain;
      gain.cancelScheduledValues(now);
      gain.setTargetAtTime(target, now, this.voiceResponse());
    }
    for (let index = 0; index < this.distanceVoices.length; index++) {
      const population = Math.min(Math.max(distanceHistogram[index] ?? 0, 0), 1);
      // Far bands receive modest loudness compensation. Their lower pitches
      // otherwise disappear on small headset speakers when an expanding
      // structure eventually collects in the final distance band.
      const farCompensation = 1 + index / 7 * 0.45;
      const target = this.distanceEnabled ? (this.mode === 'musical'
        ? Math.sqrt(population) * 0.16 * farCompensation
        : population * 0.72 * farCompensation) : 0;
      const gain = this.distanceVoices[index].gain.gain;
      gain.cancelScheduledValues(now);
      gain.setTargetAtTime(target, now, this.voiceResponse());
    }
    if (this.mode === 'musical') this.updateMusicalExpression(velocityHistogram, distanceHistogram, now);
    this.previousVelocity.set(velocityHistogram);
    this.previousDistance.set(distanceHistogram);
    this.hasPreviousHistogram = true;
  }

  suspendForBackground(): void {
    if (this.context?.state === 'running') void this.context.suspend();
  }

  private ensureGraph(): void {
    if (this.context) return;
    const AudioContextClass = window.AudioContext
      ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) throw new Error('Web Audio is unavailable in this browser.');
    this.context = new AudioContextClass({ latencyHint: 'interactive' });
    this.master = this.context.createGain();
    this.master.gain.value = 0;
    this.velocityBus = this.context.createGain();
    this.distanceBus = this.context.createGain();
    this.velocityFilter = this.context.createBiquadFilter();
    this.distanceFilter = this.context.createBiquadFilter();
    this.velocityBus.gain.value = 1;
    this.distanceBus.gain.value = 0;
    this.velocityFilter.type = 'lowpass';
    this.distanceFilter.type = 'lowpass';
    const compressor = this.context.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 18;
    compressor.ratio.value = 5;
    compressor.attack.value = 0.015;
    compressor.release.value = 0.3;
    this.recordingDestination = this.context.createMediaStreamDestination();
    this.master.connect(compressor);
    compressor.connect(this.context.destination);
    compressor.connect(this.recordingDestination);
    this.velocityBus.connect(this.velocityFilter).connect(this.master);
    this.distanceBus.connect(this.distanceFilter).connect(this.master);

    // A short filtered feedback delay gives the synthesized voices a pad-like
    // tail without loading or decoding an impulse-response audio asset.
    this.velocityAmbienceSend = this.context.createGain();
    this.distanceAmbienceSend = this.context.createGain();
    this.ambienceWet = this.context.createGain();
    const delay = this.context.createDelay(1);
    const feedback = this.context.createGain();
    const feedbackFilter = this.context.createBiquadFilter();
    delay.delayTime.value = 0.29;
    feedback.gain.value = 0.22;
    feedbackFilter.type = 'lowpass';
    feedbackFilter.frequency.value = 1800;
    this.velocityAmbienceSend.gain.value = 0;
    this.distanceAmbienceSend.gain.value = 0;
    this.ambienceWet.gain.value = 0;
    this.velocityFilter.connect(this.velocityAmbienceSend);
    this.distanceFilter.connect(this.distanceAmbienceSend);
    this.velocityAmbienceSend.connect(delay);
    this.distanceAmbienceSend.connect(delay);
    delay.connect(feedbackFilter).connect(feedback).connect(delay);
    delay.connect(this.ambienceWet).connect(this.master);
    this.setVelocityBusLevel();
    for (let index = 0; index < 16; index++) {
      const primary = this.context.createOscillator();
      const secondary = this.context.createOscillator();
      const secondaryLevel = this.context.createGain();
      const gain = this.context.createGain();
      const panner = this.context.createStereoPanner();
      panner.pan.value = index < 8 ? 0.28 : -0.48;
      secondaryLevel.gain.value = 0;
      gain.gain.value = 0;
      primary.connect(gain);
      secondary.connect(secondaryLevel).connect(gain);
      gain.connect(panner).connect(index < 8 ? this.velocityBus : this.distanceBus);
      primary.start();
      secondary.start();
      (index < 8 ? this.voices : this.distanceVoices).push({ primary, secondary, secondaryLevel, gain, panner });
    }
    for (let index = 0; index < 8; index++) {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      const panner = this.context.createStereoPanner();
      oscillator.type = 'sine';
      oscillator.frequency.value = MUSICAL_FREQUENCIES[index] * 2;
      gain.gain.value = 0;
      panner.pan.value = -0.7 + index / 7 * 1.4;
      oscillator.connect(gain).connect(panner).connect(this.master);
      oscillator.start();
      this.transientVoices.push({ oscillator, gain });
    }
    const accentOscillator = this.context.createOscillator();
    const accentGain = this.context.createGain();
    accentOscillator.type = 'triangle';
    accentOscillator.frequency.value = 220;
    accentGain.gain.value = 0;
    accentOscillator.connect(accentGain).connect(this.master);
    accentOscillator.start();
    this.accentVoice = { oscillator: accentOscillator, gain: accentGain };
  }

  private applyMode(): void {
    if (!this.context) return;
    const frequencies = this.mode === 'musical' ? this.musicalFrequencies : SCIENTIFIC_FREQUENCIES;
    const distanceFrequencies = this.mode === 'musical' ? this.musicalDistanceFrequencies : SCIENTIFIC_DISTANCE_FREQUENCIES;
    const now = this.context.currentTime;
    const musical = this.mode === 'musical';
    const velocityProfile = musical ? this.velocityProfile() : this.sineProfile();
    const distanceProfile = musical ? this.distanceProfile() : this.sineProfile();
    this.voices.forEach((voice, index) => {
      voice.primary.type = velocityProfile.primary;
      voice.secondary.type = velocityProfile.secondary;
      this.tuneVoice(voice, frequencies[index], now, -velocityProfile.detune, velocityProfile.detune, velocityProfile.secondaryRatio);
      this.setParam(voice.secondaryLevel.gain, velocityProfile.secondaryLevel, now, 0.06);
    });
    this.distanceVoices.forEach((voice, index) => {
      voice.primary.type = distanceProfile.primary;
      voice.secondary.type = distanceProfile.secondary;
      this.tuneVoice(voice, distanceFrequencies[index], now, -distanceProfile.detune, distanceProfile.detune, distanceProfile.secondaryRatio);
      this.setParam(voice.secondaryLevel.gain, distanceProfile.secondaryLevel, now, 0.06);
    });
    this.currentInversion = -1;
    this.setParam(this.velocityFilter?.frequency, velocityProfile.cutoff, now, 0.08);
    this.setParam(this.distanceFilter?.frequency, distanceProfile.cutoff, now, 0.08);
    this.setParam(this.velocityFilter?.Q, velocityProfile.resonance, now, 0.08);
    this.setParam(this.distanceFilter?.Q, distanceProfile.resonance, now, 0.08);
    this.setParam(this.velocityAmbienceSend?.gain, velocityProfile.ambience, now, 0.12);
    this.setParam(this.distanceAmbienceSend?.gain, distanceProfile.ambience, now, 0.12);
    this.setParam(this.ambienceWet?.gain, musical ? 0.28 : 0, now, 0.12);
  }

  private setMasterLevel(value: number): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(value, now, 0.035);
  }

  private setDistanceBusLevel(): void {
    if (!this.context || !this.distanceBus) return;
    const now = this.context.currentTime;
    const level = this.distanceEnabled ? this.distanceMix * 1.5 : 0;
    this.distanceBus.gain.cancelScheduledValues(now);
    this.distanceBus.gain.setTargetAtTime(level, now, 0.04);
  }

  private setVelocityBusLevel(): void {
    if (!this.context || !this.velocityBus) return;
    const now = this.context.currentTime;
    this.velocityBus.gain.cancelScheduledValues(now);
    this.velocityBus.gain.setTargetAtTime(this.velocityEnabled ? 1 : 0, now, 0.04);
  }

  private tuneVoice(
    voice: Voice, frequency: number, now: number,
    primaryDetune: number, secondaryDetune: number, secondaryRatio = 1
  ): void {
    voice.primary.frequency.setTargetAtTime(frequency, now, 0.04);
    voice.secondary.frequency.setTargetAtTime(frequency * secondaryRatio, now, 0.04);
    voice.primary.detune.setTargetAtTime(primaryDetune, now, 0.04);
    voice.secondary.detune.setTargetAtTime(secondaryDetune, now, 0.04);
  }

  private velocityProfile(): TimbreProfile {
    switch (this.velocityTimbre) {
      case 'brightPad': return { primary: 'triangle', secondary: 'sawtooth', secondaryRatio: 1, secondaryLevel: 0.2, detune: 3.5, cutoff: 2600, resonance: 1.05, ambience: 0.4 };
      case 'deepSpace': return { primary: 'triangle', secondary: 'sine', secondaryRatio: 0.5, secondaryLevel: 0.28, detune: 2.5, cutoff: 900, resonance: 0.75, ambience: 0.38 };
      case 'choir': return { primary: 'sine', secondary: 'square', secondaryRatio: 2, secondaryLevel: 0.08, detune: 2, cutoff: 1650, resonance: 1.6, ambience: 0.46 };
      case 'shimmer': return { primary: 'sine', secondary: 'sine', secondaryRatio: 2, secondaryLevel: 0.24, detune: 4, cutoff: 5200, resonance: 0.5, ambience: 0.58 };
      case 'glass': return { primary: 'sine', secondary: 'triangle', secondaryRatio: 2, secondaryLevel: 0.2, detune: 2, cutoff: 6200, resonance: 1.25, ambience: 0.44 };
      case 'darkDrone': return { primary: 'triangle', secondary: 'sawtooth', secondaryRatio: 0.5, secondaryLevel: 0.14, detune: 3, cutoff: 680, resonance: 0.9, ambience: 0.34 };
      case 'sine': return this.sineProfile();
      case 'softPad':
      default: return { primary: 'sine', secondary: 'triangle', secondaryRatio: 1, secondaryLevel: 0.38, detune: 2.5, cutoff: 1450, resonance: 0.7, ambience: 0.32 };
    }
  }

  private distanceProfile(): TimbreProfile {
    switch (this.distanceTimbre) {
      case 'choir': return { primary: 'sine', secondary: 'square', secondaryRatio: 2, secondaryLevel: 0.07, detune: 2, cutoff: 1450, resonance: 1.7, ambience: 0.46 };
      case 'shimmer': return { primary: 'sine', secondary: 'sine', secondaryRatio: 2, secondaryLevel: 0.22, detune: 4, cutoff: 4800, resonance: 0.55, ambience: 0.55 };
      case 'deep': return { primary: 'triangle', secondary: 'sine', secondaryRatio: 0.5, secondaryLevel: 0.26, detune: 2.5, cutoff: 760, resonance: 0.8, ambience: 0.35 };
      case 'sine': return this.sineProfile();
      case 'warm':
      default: return { primary: 'triangle', secondary: 'sine', secondaryRatio: 1, secondaryLevel: 0.34, detune: 2.5, cutoff: 1100, resonance: 0.85, ambience: 0.28 };
    }
  }

  private sineProfile(): TimbreProfile {
    return { primary: 'sine', secondary: 'sine', secondaryRatio: 1, secondaryLevel: 0, detune: 0, cutoff: 12000, resonance: 0, ambience: 0 };
  }

  private setParam(param: AudioParam | undefined, value: number, now: number, response: number): void {
    if (!param) return;
    param.cancelScheduledValues(now);
    param.setTargetAtTime(value, now, response);
  }

  private voiceResponse(): number {
    return this.mode === 'musical' ? Math.max(this.response, 0.16) : this.response;
  }

  private updateMusicalExpression(velocity: Float32Array, distance: Float32Array, now: number): void {
    const movement = this.musicalMovement;
    let velocityMean = 0;
    let distanceMean = 0;
    let dominantBand = 0;
    let dominantPopulation = -1;
    for (let index = 0; index < 8; index++) {
      const position = index / 7;
      velocityMean += (velocity[index] ?? 0) * position;
      distanceMean += (distance[index] ?? 0) * position;
      if ((velocity[index] ?? 0) > dominantPopulation) {
        dominantPopulation = velocity[index] ?? 0;
        dominantBand = index;
      }
    }
    let variance = 0;
    for (let index = 0; index < 8; index++) {
      const delta = index / 7 - velocityMean;
      variance += (velocity[index] ?? 0) * delta * delta;
    }
    const spread = Math.min(Math.sqrt(variance) * 2.2, 1);
    const velocityProfile = this.velocityProfile();
    const distanceProfile = this.distanceProfile();
    const velocityCutoff = velocityProfile.cutoff >= 10000
      ? velocityProfile.cutoff
      : velocityProfile.cutoff + movement * (velocityMean * 2600 + spread * 900);
    const distanceCutoff = distanceProfile.cutoff >= 10000
      ? distanceProfile.cutoff
      : distanceProfile.cutoff + movement * (1 - distanceMean) * 1700;
    this.setParam(this.velocityFilter?.frequency, velocityCutoff, now, 0.2);
    this.setParam(this.distanceFilter?.frequency, distanceCutoff, now, 0.2);
    for (const voice of this.voices) {
      const richness = velocityProfile.secondaryLevel * (0.72 + movement * (0.18 + spread * 0.45));
      this.setParam(voice.secondaryLevel.gain, richness, now, 0.18);
    }
    for (const voice of this.distanceVoices) {
      const richness = distanceProfile.secondaryLevel * (0.78 + movement * (1 - distanceMean) * 0.35);
      this.setParam(voice.secondaryLevel.gain, richness, now, 0.18);
    }

    const inversion = dominantBand >= 6 ? 2 : dominantBand >= 3 ? 1 : 0;
    this.currentInversion = inversion;
    const raisedVoices = inversion * 2;
    const rootMorph = Math.pow(2, ((velocityMean - 0.5) * movement * 2) / 12);
    // Keep both layers in the same slowly moving key. Distance continues to
    // shape filtering and dynamics without independently bending pitch.
    const distanceMorph = rootMorph;
    this.voices.forEach((voice, index) => {
      const frequency = this.musicalFrequencies[index] * (index < raisedVoices ? 2 : 1) * rootMorph;
      voice.primary.frequency.setTargetAtTime(frequency, now, 0.42);
      voice.secondary.frequency.setTargetAtTime(frequency * velocityProfile.secondaryRatio, now, 0.42);
    });
    this.distanceVoices.forEach((voice, index) => {
      const frequency = this.musicalDistanceFrequencies[index] * distanceMorph;
      voice.primary.frequency.setTargetAtTime(frequency, now, 0.42);
      voice.secondary.frequency.setTargetAtTime(frequency * distanceProfile.secondaryRatio, now, 0.42);
    });

    if (!this.hasPreviousHistogram || movement <= 0.001) return;
    let eventBand = 0;
    let eventStrength = 0;
    let distanceEvent = false;
    let totalFlux = 0;
    for (let index = 0; index < 8; index++) {
      const velocityDelta = (velocity[index] ?? 0) - this.previousVelocity[index];
      const distanceDelta = (distance[index] ?? 0) - this.previousDistance[index];
      totalFlux += Math.abs(velocityDelta) + Math.abs(distanceDelta) * this.distanceMix;
      if (velocityDelta > eventStrength) {
        eventStrength = velocityDelta;
        eventBand = index;
        distanceEvent = false;
      }
      if (this.distanceEnabled && distanceDelta * this.distanceMix > eventStrength) {
        eventStrength = distanceDelta * this.distanceMix;
        eventBand = index;
        distanceEvent = true;
      }
    }
    const activity = Math.min(totalFlux * 5, 1);
    const accelerationSurge = (velocity[6] + velocity[7]) - (this.previousVelocity[6] + this.previousVelocity[7]);
    const closeSurge = (distance[0] + distance[1]) - (this.previousDistance[0] + this.previousDistance[1]);
    if (this.rareAccentsEnabled && now >= this.nextAccentTime && Math.max(accelerationSurge, closeSurge) > 0.012) {
      this.triggerAccent(accelerationSurge >= closeSurge, Math.max(accelerationSurge, closeSurge), now);
    }
    if (this.eventDensity <= 0.001 || now < this.nextTransientTime) return;
    const eventThreshold = 0.0035 - this.eventDensity * 0.0027;
    if (eventStrength < eventThreshold || activity < 0.005) return;
    const voice = this.transientVoices[eventBand];
    if (!voice) return;
    const baseFrequency = distanceEvent ? this.musicalDistanceFrequencies[eventBand] : this.musicalFrequencies[eventBand];
    const octave = inversion === 2 && eventBand < 4 ? 2 : 1;
    voice.oscillator.frequency.setTargetAtTime(baseFrequency * octave * 2, now, 0.015);
    const peak = Math.min(0.025 + Math.sqrt(eventStrength) * 0.42, 0.2) * movement * (0.55 + this.eventDensity * 0.65);
    const release = 0.18 + (1 - movement) * 0.48;
    voice.gain.gain.cancelScheduledValues(now);
    voice.gain.gain.setValueAtTime(0.0001, now);
    voice.gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), now + 0.012);
    voice.gain.gain.exponentialRampToValueAtTime(0.0001, now + release);
    this.nextTransientTime = now + 0.08 + (1 - this.eventDensity) * 0.75 + (1 - movement) * 0.22 + (1 - activity) * 0.18;
  }

  private triggerAccent(acceleration: boolean, strength: number, now: number): void {
    if (!this.accentVoice) return;
    const root = PALETTES[this.palette].root;
    this.accentVoice.oscillator.type = acceleration ? 'sine' : 'triangle';
    this.accentVoice.oscillator.frequency.setTargetAtTime(acceleration ? root * 8 : root * 3, now, 0.02);
    const peak = Math.min(0.07 + Math.sqrt(strength) * 0.32, 0.2) * this.musicalMovement;
    this.accentVoice.gain.gain.cancelScheduledValues(now);
    this.accentVoice.gain.gain.setValueAtTime(0.0001, now);
    this.accentVoice.gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), now + (acceleration ? 0.01 : 0.035));
    this.accentVoice.gain.gain.exponentialRampToValueAtTime(0.0001, now + (acceleration ? 0.42 : 0.8));
    this.nextAccentTime = now + 1.4;
  }
}
